import { LOGIN_URL, ME_URL, LOGOUT_URL } from '../config'

/**
 * Signing in, the same way Pod Command Center does it.
 *
 * lereco issues nothing. The shared Vision service checks the password and
 * returns the user; lereco-backend reads that user out of Vision on every
 * request. So there is no sign-up here, no password reset, and no user record of
 * our own — three things that would each be a second place for an account to
 * exist.
 *
 * Two decisions in here are copied from PCC deliberately, because both were
 * learned the hard way and re-deriving them costs somebody a bad afternoon:
 *
 *   **The cached session is trusted outright on boot.** See `restoreSession`.
 *   **A 401 signs you out; a 403 must not.** See `api.js`.
 */

/** Same shape of key as PCC's `pcc-user`, scoped to this app. */
const LS_USER_KEY = 'lereco-user'

/** Shown on the login form when an account isn't allowed in. */
export const NO_ACCESS = 'This account doesn’t have access to Lereco — it’s only for admins and the accounts team.'

function loadUser() {
  try {
    const raw = localStorage.getItem(LS_USER_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function saveUser(user) {
  try {
    localStorage.setItem(LS_USER_KEY, JSON.stringify(user))
  } catch {
    /* private browsing — the session just won't survive a refresh */
  }
}

function clearUser() {
  try {
    localStorage.removeItem(LS_USER_KEY)
  } catch {
    /* nothing to clear */
  }
}

/**
 * What lereco-backend uses to identify the caller.
 *
 * `x-user-id`, exactly as PCC and the other consoles send it. The id is dug out
 * of every shape the shared auth endpoint has been seen to return, because a
 * session stored from one shape and read expecting another silently sends no
 * header at all — which arrives as "not authenticated" and looks like a password
 * problem.
 */
export function authHeaders() {
  const u = loadUser()
  const id = u?._id || u?.id || u?.data?._id || u?.data?.id
  return id ? { 'x-user-id': String(id) } : {}
}


/**
 * The shared auth endpoint answers in more than one shape.
 *
 * Sometimes `{ status, data: { _id, ... } }`, sometimes `{ status, _id, ... }` —
 * and `status` is not reliably a number. Every Binny front end has had to learn
 * this, and each one that skipped normalising stored a user with no `_id` and
 * bounced its own admins straight back to the login screen.
 */
function normalise(body) {
  const user = body?.data && (body.data._id || body.data.id) ? body.data : body
  if (!(user?._id || user?.id)) return null

  /*
   * The token is lifted from wherever it happens to be.
   *
   * It sits at the top level on the flat shape and inside `data` on the nested
   * one — so on the nested shape, returning `body.data` alone loses it silently.
   * That matters now that a Vision API is called with `Authorization: Bearer`
   * from the browser: the session would look fine, every screen would work, and
   * only that one call would 401 with nothing to explain it.
   */
  const token = user.token ?? body?.token ?? null
  return token && !user.token ? { ...user, token } : user
}

/** The bearer token for calls to Vision's own APIs. Null when there isn't one. */
export function authToken() {
  const u = loadUser()
  return u?.token ?? u?.data?.token ?? null
}

/**
 * Sign in.
 *
 * Returns the user on success, throws with a message worth showing otherwise.
 * The message is Vision's own where it has one — it says which half was wrong,
 * which is more useful than a blanket "invalid credentials" and is not a secret
 * on an internal console with no sign-up.
 *
 * `rememberMe` only sets the flag sent to Vision, which decides the token's
 * expiry from it. Local storage is localStorage either way — the same convention
 * as every other Binny app.
 */
export async function signIn({ username, password, rememberMe = true }) {
  if (!LOGIN_URL) {
    throw new Error('Sign-in is not configured — VITE_VISION_BACKEND_URL is not set.')
  }

  let res
  let body
  try {
    res = await fetch(LOGIN_URL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, rememberMe }),
      signal: AbortSignal.timeout(15000),
    })
    body = await res.json().catch(() => null)
  } catch (e) {
    throw new Error(
      e?.name === 'TimeoutError' ? 'That took too long. Try again.' : 'Could not reach the sign-in service.'
    )
  }

  const user = normalise(body)
  const ok = res.ok || body?.status === 200 || String(body?.status) === '200'
  if (!ok || !user) {
    throw new Error(body?.message || `Invalid username or password. (${res.status})`)
  }

  saveUser(user)
  return user
}

/**
 * The session to start the app with.
 *
 * **A cached session is trusted outright, and `ME_URL` is only asked when there
 * isn't one.** That looks lax and is not: `ME_URL` is on binnysjewellery.in,
 * a different site from this one, and Safari's Intelligent Tracking Prevention
 * blocks third-party cookies on cross-site fetches by default. So it reliably
 * 401s there *for a user who is genuinely still signed in* — and checking it on
 * every boot, treating the 401 as "log out", force-signed-out every valid
 * session on every refresh in Safari specifically. PCC hit exactly this and
 * settled on the order below.
 *
 * Nothing is lost by trusting it. lereco-backend reads the account live from
 * Vision on every single request, so a disabled account stops working there
 * whatever this function believed — the worst case is one 401 from our own API,
 * which signs the person out properly.
 */
export async function restoreSession() {
  const cached = loadUser()
  if (cached) return cached

  if (!ME_URL) return null

  try {
    const res = await fetch(ME_URL, {
      credentials: 'include',
      headers: authHeaders(),
      signal: AbortSignal.timeout(15000),
    })
    if (!res.ok) return null

    const user = normalise(await res.json().catch(() => null))
    if (!user) return null

    saveUser(user)
    return user
  } catch {
    return null
  }
}

/**
 * Sign out here first, tell Vision after.
 *
 * Not awaited, and failures are swallowed: the local session is gone either way.
 * A logout that can fail is a logout that can trap somebody signed in, which is
 * the one outcome this must never have.
 */
export function signOut() {
  clearUser()
  if (LOGOUT_URL) {
    fetch(LOGOUT_URL, { method: 'POST', credentials: 'include' }).catch(() => {})
  }
}

/**
 * A session handed over by Binny's One at launch.
 *
 * Binny's One is the company's app launcher — "one login, all apps" — and it
 * signs in against the *same* Vision endpoint this file does. Somebody arriving
 * from that dashboard has already proved who they are, and a second login form
 * is precisely what the launcher exists to prevent.
 *
 * Its localStorage is not readable from here (different origin), so the token
 * comes over in the URL — **in the fragment, never the query string**. `#token=`
 * is never sent to a server, never lands in an access log, and is not passed on
 * in a `Referer`; `?token=` is all three, which would scatter a live credential
 * across every log between here and the CDN. It is read once, exchanged for a
 * user, and erased from the address bar before anything else runs.
 *
 * The token is not trusted because it arrived. It is exchanged at `ME_URL`,
 * which is what stops a crafted link signing somebody in as anyone.
 */
export async function adoptHandoffToken() {
  const hash = window.location.hash || ''
  if (!hash.includes('token=')) return null

  const token = new URLSearchParams(hash.replace(/^#/, '')).get('token')

  // Erased first, and unconditionally. A token that turns out to be invalid must
  // not stay in the address bar to be copied into a chat window, and
  // `replaceState` keeps it out of the back button too.
  window.history.replaceState(null, '', window.location.pathname + window.location.search)

  if (!token || !ME_URL) return null

  try {
    const res = await fetch(ME_URL, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
      signal: AbortSignal.timeout(15000),
    })
    if (!res.ok) return null

    const user = normalise(await res.json().catch(() => null))
    if (!user) return null

    saveUser(user)
    return user
  } catch {
    // Vision unreachable. Falls through to the normal path, which shows the
    // login form — the right outcome, since nothing here could be verified.
    return null
  }
}
